import {
  createSessionStores,
  defaultSiteEnv,
  type EnvFallback,
} from '@sparklayer-io/dashboard/store/session';
import type { Env } from '@sparklayer-io/spark-dash-core/context/env-context';
import { apiHandler, setCurrentSite } from './api-handler';

export type {
  EnvFallback,
  HostAvailableTeam,
  HostUser,
} from '@sparklayer-io/dashboard/store/session';
export type { Env };
export { defaultSiteEnv };

// The composition root: spark-dash is the one host wiring the auth/env/settings stores to its own
// `apiHandler`. Everything else in the app imports the resulting hooks from here rather than
// reaching into `@sparklayer-io/dashboard/store/session` directly.
export const { useAuthStore, useEnvStore, useSettingsStore } = createSessionStores(apiHandler);

// The proxy (`sparkApi`) endpoints are addressed by the site the env store currently holds. Mirror
// it into the api handler so a proxy call made *while* the env is loading - `loadEnv` fetches
// settings through the proxy once the env resolves, before any route guard runs `setCurrentSite` -
// still has a site to target. Zustand fires this synchronously inside the store's `set`, so the
// mirror is in place before that settings call is made.
useEnvStore.subscribe(({ siteId, siteEnv }) => {
  setCurrentSite(siteId && siteEnv ? { siteId, siteEnv } : null);
});

/**
 * Ensure the session user is loaded (or reuse what an earlier call this page load already fetched)
 * and return it. Every route guard that needs the user goes through this.
 */
export async function loadSessionUser() {
  await useAuthStore.getState().loadUser();
  return useAuthStore.getState().data;
}

/**
 * Ensure the env for a site/environment is loaded and return it, falling back to the other
 * environment when the site rejects the one asked for (see {@link EnvFallback}).
 */
export async function loadSessionEnv(
  siteId: string,
  siteEnv: string,
  fallback?: EnvFallback,
  options: { quiet?: boolean } = {},
): Promise<Env | null> {
  await useEnvStore.getState().loadEnv({ siteId, siteEnv }, { fallback, quiet: options.quiet });
  return useEnvStore.getState().env;
}

/** Convenience accessors the guards read off a resolved env. */
export function isSiteReady(env: Env | null): boolean {
  return !!env?.db_created;
}

/**
 * The site/env pair the session actually has loaded. A failed load keeps the `siteId` it was for
 * but no `env`, and must not count.
 */
export function loadedSite(): { siteId: string; siteEnv: string } | null {
  const { siteId, siteEnv, env } = useEnvStore.getState();
  return env && siteId && siteEnv ? { siteId, siteEnv } : null;
}
